import { memo } from 'react';
import type { ObjectDefinitionSpec, ObjectShape } from 'shared-types';
import { T, useT } from '../../../translation/src';
import { pinBlocker, pinNode, pinStateOf, type PinAxis, type PinMode } from '../document/pins';
import { addParam, uniqueParamKey } from '../document/specOps';
import { useObjectEditor } from '../EditorContext';
import { FieldRow, NumberField, PanelButton, PanelSection, SelectField } from '../panels/fields';

const FIXED = '__fixed';

/** One axis: how the point follows the size, and its distance (typed, or a parameter users can set). */
const AxisPin: React.FC<{ spec: ObjectDefinitionSpec; shape: ObjectShape; index: number; axis: PinAxis }> = ({ spec, shape, index, axis }) => {
  const { store } = useObjectEditor();
  const labels: Record<PinMode, string> = {
    stretch: useT('stretches with the object', { swc: true }),
    start: useT(axis === 'u' ? 'from the left edge' : 'from the back edge', { swc: true }),
    end: useT(axis === 'u' ? 'from the right edge' : 'from the front edge', { swc: true }),
    center: useT('from the middle', { swc: true }),
  };
  const fixedLabel = useT('fixed distance', { swc: true });
  const newParamLabel = useT('distance', { swc: false });
  const state = pinStateOf(spec, shape.id, index, axis);
  if (!state) return null;
  const pin = (mode: PinMode, distance?: number | string) => store.apply((s) => pinNode(s, shape.id, index, axis, mode, distance));

  if (state.mode === 'custom')
    return (
      <FieldRow label={axis === 'u' ? <T _str="across the width" swc /> : <T _str="across the depth" swc />} hint={<T _str="a custom formula (see the shape's formulas); pick a rule to replace it" swc />}>
        <SelectField value="" options={[{ value: '', label: state.formula === 'float' ? '…' : state.formula }, ...(['stretch', 'start', 'end', 'center'] as const).map((m) => ({ value: m, label: labels[m] }))]} onChange={(m) => m && pin(m)} />
      </FieldRow>
    );

  const distanceParams = spec.params.filter((p) => p.kind === 'float');
  const isStretch = state.mode === 'stretch';
  return (
    <FieldRow label={axis === 'u' ? <T _str="across the width" swc /> : <T _str="across the depth" swc />}>
      <span className="flex flex-col gap-1">
        <SelectField value={state.mode} options={(['stretch', 'start', 'end', 'center'] as const).map((m) => ({ value: m, label: labels[m] }))} onChange={(m) => pin(m)} />
        {typeof state.distance === 'number' ? (
          <span className="flex items-center gap-1">
            <span className="min-w-0 flex-1">
              {isStretch ? (
                <NumberField value={state.distance * 100} unit="%" decimals={1} onCommit={(v) => pin('stretch', v / 100)} />
              ) : (
                <NumberField value={state.distance} unit="m" onCommit={(v) => pin(state.mode, v)} />
              )}
            </span>
            {!isStretch && (
              // A dimension users can change on placed objects: the distance becomes a parameter.
              <PanelButton
                title={labels[state.mode]}
                onClick={() => {
                  const key = uniqueParamKey(spec, 'distance');
                  const distance = state.distance;
                  store.apply((s) => pinNode(addParam(s, { key, kind: 'float', label: newParamLabel, default: typeof distance === 'number' ? Math.abs(distance) : 0.1, min: 0, decimals: 2, distance: true }), shape.id, index, axis, state.mode, key));
                }}
              >
                <T _str="make adjustable" swc />
              </PanelButton>
            )}
          </span>
        ) : (
          <SelectField
            value={state.distance}
            options={[...distanceParams.map((p) => ({ value: p.key, label: p.label })), { value: FIXED, label: fixedLabel }]}
            onChange={(v) => {
              const param = spec.params.find((p) => p.key === state.distance);
              pin(state.mode, v === FIXED ? (param && typeof param.default === 'number' ? param.default : 0) : v);
            }}
          />
        )}
      </span>
    </FieldRow>
  );
};

/**
 * The picked point of the Edit points tool: how it follows the object's size
 * — stretch along, or keep a distance to an edge (typed, or a parameter).
 * Writes the shape's formulas (document/pins.ts), so the formulas panel
 * shows exactly what was set here.
 */
const PointPins_: React.FC<{ spec: ObjectDefinitionSpec; shape: ObjectShape; index: number }> = ({ spec, shape, index }) => {
  const blocker = pinBlocker(spec, shape.id);
  return (
    <PanelSection title={<T _str="point {n}" args={{ n: index + 1 }} />}>
      {blocker ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {blocker === 'repeat' ? (
            <T _str="points of repeated shapes follow their repeat; ungroup the shape to pin its points" swc />
          ) : blocker === 'round' ? (
            <T _str="circles and arcs that stretch become ellipses and can't be pinned; set their group to keep its size first" swc />
          ) : (
            <T _str="curve points can't be pinned" swc />
          )}
        </p>
      ) : (
        <>
          <AxisPin spec={spec} shape={shape} index={index} axis="u" />
          <AxisPin spec={spec} shape={shape} index={index} axis="n" />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <T _str="how this point moves when a placed object is resized; 'make adjustable' turns the distance into a parameter users can set" swc />
          </p>
        </>
      )}
    </PanelSection>
  );
};

export const PointPins = memo(PointPins_);
